package horcrux.common.api.dtos.types

import arrow.core.Either
import arrow.core.raise.either
import arrow.optics.optics
import com.benasher44.uuid.Uuid
import com.benasher44.uuid.uuid4
import com.benasher44.uuid.uuidFrom
import horcrux.common.HorcruxEither
import horcrux.common.HorcruxRequestSimpleError
import horcrux.common.URLValueClass
import horcrux.common.UniqueValueClass
import horcrux.common.UuidSerializer
import horcrux.common.ValidatedValueClass
import horcrux.common.ValueClass
import horcrux.common.api.APILimits
import horcrux.common.api.dtos.CollectionPrivacyType
import horcrux.common.api.dtos.EmbeddingModelID
import horcrux.common.configs.DocumentStorageConfigs
import horcrux.common.utils.EnumCompanion
import horcrux.common.utils.StringEnum
import io.ktor.http.HttpStatusCode
import kotlinx.datetime.Instant
import kotlinx.serialization.Serializable
import kotlin.jvm.JvmInline

@Serializable
@JvmInline
value class PutSignedDocumentURL(override val value: String) : URLValueClass<PutSignedDocumentURL>

@Serializable
@JvmInline
value class ParameterName(override val value: String) : ValidatedValueClass<String, ParameterName>

@Serializable
@JvmInline
value class ChunkSize(override val value: Int) : ValidatedValueClass<Int, ChunkSize>

@Serializable
@JvmInline
value class ChunkOverlap(override val value: Int) : ValidatedValueClass<Int, ChunkOverlap>

@Serializable
data class EmbeddingModelConfigs(
    val maxDocumentChunkSize: ChunkSize,
    val maxDocumentChunkOverlap: ChunkOverlap,
)

@Serializable
@JvmInline
value class RawDocumentID(@Serializable(with = UuidSerializer::class) override val value: Uuid) :
    ValidatedValueClass<Uuid, RawDocumentID> {
    constructor() : this(uuid4())

    val authenticatedURL: DocumentURL
        get() = DocumentURL("${DocumentStorageConfigs.AUTHENTICATED_URL_BASE}/${DocumentStorageConfigs.PUBLIC_GCS_BUCKET.betterToString()}/${DocumentStorageConfigs.RAW_DOCUMENTS_BASE_PATH}/${betterToString()}")

    val cloudStorageKey: String
        get() = "${DocumentStorageConfigs.RAW_DOCUMENTS_BASE_PATH}/${betterToString()}"
}

@Serializable
@JvmInline
value class DocumentID(@Serializable(with = UuidSerializer::class) override val value: Uuid) :
    ValidatedValueClass<Uuid, DocumentID> {
    companion object {
        operator fun invoke(value: String): Either<Throwable, DocumentID> = Either.catch {
            DocumentID(uuidFrom(value))
        }

        operator fun invoke(): DocumentID = DocumentID(uuid4())
    }
}

data class InvalidCollectionName(val collectionName: CollectionName, override val cause: Throwable) :
    HorcruxRequestSimpleError(cause, HttpStatusCode.UnprocessableEntity)

fun String.truncateAndMakeUnique(maxLength: Int, numRandomChars: Int): String =
    "${this.take(maxLength - numRandomChars)}-${uuid4().toString().take(numRandomChars)}"

@JvmInline
@Serializable
value class ChatPageTitle(override val value: String) : ValidatedValueClass<String, CollectionName> {

    companion object {
        operator fun invoke(collectionName: CollectionName): ChatPageTitle =
            ChatPageTitle("The Grok App: ${collectionName.betterToString()}")

        val NOT_FOUND: ChatPageTitle = ChatPageTitle(CollectionName("Chat Page Not Found"))
    }
}

@JvmInline
@Serializable
value class CollectionName(override val value: String) : UniqueValueClass<String, CollectionName> {
    override fun validate(): HorcruxEither<CollectionName> = Either.catch {
        require(this.value.length in APILimits.MIN_COLLECTION_NAME_LENGTH..APILimits.MAX_COLLECTION_NAME_LENGTH) {
            "CollectionName must be between ${APILimits.MIN_COLLECTION_NAME_LENGTH} and ${APILimits.MAX_DOCUMENT_NAME_LENGTH} characters"
        }
        this
    }.mapLeft { InvalidCollectionName(this, it) }

    val chatPageTitle: ChatPageTitle
        get() = ChatPageTitle(this)

    companion object {
        fun getUniqueCollectionNameFromValue(value: String, collectionNames: Set<CollectionName>): CollectionName {
            val collectionName = CollectionName(value.take(APILimits.MAX_COLLECTION_NAME_LENGTH))
            if (collectionNames.contains(collectionName)) {
                return CollectionName(value.truncateAndMakeUnique(APILimits.MAX_COLLECTION_NAME_LENGTH, 4))
            }
            return collectionName
        }
    }
}

@JvmInline
@Serializable
value class Question(override val value: String) : TextDocument {
    override fun toString(): String = value.take(100)
}

@JvmInline
@Serializable
value class Answer(override val value: String) : TextDocument {
    override fun toString(): String = value.take(100)
}

@JvmInline
@Serializable
value class NumChunks(override val value: Int) : ValueClass<Int, NumChunks> {
    override fun validate(): HorcruxEither<NumChunks> = either {
        require(value in APILimits.MIN_NUM_CHUNKS..APILimits.MAX_NUM_CHUNKS) { "NumChunks must be between ${APILimits.MIN_NUM_CHUNKS} and ${APILimits.MAX_NUM_CHUNKS}" }
        this@NumChunks
    }

}

@JvmInline
@Serializable
value class DocumentChunkIndex(override val value: Int) : ValidatedValueClass<Int, DocumentChunkIndex> {
    companion object {
        fun create(documentChunkIndex: String): HorcruxEither<DocumentChunkIndex> = Either.catch {
            DocumentChunkIndex(documentChunkIndex.toInt())
        }

        operator fun invoke(value: String): Either<Throwable, DocumentChunkIndex> = Either.catch {
            DocumentChunkIndex(value.toInt())
        }

        val ZERO: DocumentChunkIndex = DocumentChunkIndex(0)
    }
}

@JvmInline
@Serializable
value class RelevancyScore(override val value: Float) : ValidatedValueClass<Float, RelevancyScore>

@JvmInline
@Serializable
value class UserFeedback(override val value: String) : ValueClass<String, UserFeedback> {
    override fun validate(): HorcruxEither<UserFeedback> = Either.catch {
        require(value.length in APILimits.MIN_USER_FEEDBACK_SIZE..APILimits.MAX_USER_FEEDBACK_SIZE)
        this
    }
}

@Serializable
@JvmInline
value class EmailAddress(override val value: String) : ValueClass<String, EmailAddress> {
    override fun validate(): HorcruxEither<EmailAddress> = Either.catch {
        require(validEmailRegExp.matches(value)) { "Must be valid email" }
        this
    }

    override fun toString(): String = "EmailAddress<***>"

    companion object {
        private val validEmailRegExp =
            Regex("^[a-zA-Z0-9_+&*-]+(?:\\.[a-zA-Z0-9_+&*-]+)*@(?:[a-zA-Z0-9-]+\\.)+[a-zA-Z]{2,7}$")
    }
}

@Serializable
enum class ChatModelID(val modelID: String, val documentation: String) : StringEnum {
    GPT_3_5_TURBO("gpt-3.5-turbo", "A powerful large language model that can accept up to 16,385 tokens."), GPT_4O_MINI(
        "gpt-4o-mini",
        "Affordable and intelligent small model for fast, lightweight tasks. Accepts up to 128,000 tokens."
    ),
    GPT_4O("gpt-4o", "High-intelligence flagship model for complex, multi-step tasks. Accepts up to 128,000 tokens.");

    override val stringValue: String = modelID

    companion object : EnumCompanion<ChatModelID> {
        override val valuesList: List<ChatModelID> = enumValues<ChatModelID>().toList()
        val BY_STRING: Map<String, ChatModelID> = valuesList.associateBy { it.modelID }

        fun getFormattedDocumentationString(): String {
            val stringBuilder = StringBuilder()
            for (chatModelID in valuesList) {
                stringBuilder.append("${chatModelID.modelID}: ${chatModelID.documentation}\n")
            }
            return stringBuilder.toString()
        }
    }
}

@Serializable
enum class DocumentPrivacyType {
    PRIVATE, PUBLIC;
}

@Serializable
data class ChatModelConfigs(
    val maxNumChunks: NumChunks,
    val chatModelID: ChatModelID,
)

@Serializable
data class DocumentChunkID(val documentID: DocumentID, val documentChunkIndex: DocumentChunkIndex) {
    companion object {
        val PARAMETER_NAME: ParameterName = ParameterName("DocumentChunkID")
        operator fun invoke(value: String): Either<Throwable, DocumentChunkID> = either {
            val components = value.split("-")
            val documentID =
                DocumentID(components.getOrElse(0) { raise(IllegalStateException("Component DocumentID was not found")) }).bind()
            val documentChunkIndex =
                DocumentChunkIndex(components.getOrElse(1) { raise(IllegalStateException("Component DocumentChunkIndex was not found")) }).bind()
            DocumentChunkID(documentID, documentChunkIndex)
        }
    }

    fun betterToString(): String = "${documentID.betterToString()}-${documentChunkIndex.betterToString()}"
}

@Serializable
data class ContextDocumentChunkAndMetadata(
    val documentChunk: DocumentChunk,
    val documentMetadata: DocumentMetadata,
    val documentChunkIndex: DocumentChunkIndex,
    val relevancyScore: RelevancyScore,
) {
    val documentChunkID: DocumentChunkID by lazy {
        DocumentChunkID(documentMetadata.documentID, documentChunkIndex)
    }
}

interface HasID<ID> {
    val id: ID
}

@Serializable
@optics
data class CollectionMetadata(
    override val id: CollectionID,
    val collectionName: CollectionName,
    val createdAt: Instant,
    val collectionPrivacyType: CollectionPrivacyType,
    val embeddingModelID: EmbeddingModelID,
) : HasID<CollectionID> {
    companion object
}

@Serializable
@JvmInline
value class RawDocumentName(override val value: String) : ValidatedValueClass<String, RawDocumentName>

@JvmInline
value class UserID(override val value: String) : ValidatedValueClass<String, UserID> {
    companion object {
        val NOT_LOGGED_IN_USER_ID: UserID = UserID("NOT_LOGGED_IN")
    }
}

data class DocumentIDAndName(val documentID: DocumentID, val documentName: DocumentName)

@Serializable
@JvmInline
value class DocumentChunkURL(override val value: String) : URLValueClass<DocumentChunkURL>
